import React, { useCallback } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import CONFIG from "config/config";
import { EDDM_SIGNUP } from "shared/constants";
import { ThemedOadsSidebar } from "campaign-builder/ThemedOadsSidebar";
import { oadsSidebarFooterItems } from "campaign-builder/oadsSidebarFooter";

interface EddmOadsSidebarProps {
  country?: { code?: string } | null;
  client?: { isSelfServeEnabled?: boolean; hasDmCoreCampaigns?: boolean; createdBy?: string } | null;
  /** See ThemedOadsSidebar — the segments page always lands collapsed to leave room for the map. */
  collapseOnMount?: boolean;
}

/**
 * Redesigned oads-sidebar wired for the legacy EDDM pages (segments / submit).
 * Mirrors the composition the redesigned channels use (see AdmBuilder): it
 * derives clientId + currentPath from the router and forwards the cross-frontend
 * base URLs, so the EDDM pages only have to hand over `country` and `client`
 * (which they already read from the store).
 */
export const EddmOadsSidebar = ({ country, client, collapseOnMount }: EddmOadsSidebarProps) => {
  const { clientId } = useParams<{ clientId: string }>();
  const { pathname } = useLocation();
  const isEddmOnly = client?.createdBy === EDDM_SIGNUP;

  // The oads-sidebar menu spans all three frontends. Cross-frontend items
  // (platform / next) are absolute URLs → full-page <a>; builder-local routes
  // navigate client-side via react-router.
  const renderSidebarLink = useCallback(
    ({ href, className, children }: { href: string; className?: string; children: React.ReactNode }) => {
      // Normalize a possible trailing slash on the base so stripping it keeps the
      // leading "/" of the route (else react-router treats it as a relative path).
      const builderBase = CONFIG.BUILDER_BASE_URL.replace(/\/+$/, "");
      const isExternal = /^https?:\/\//i.test(href) && !href.startsWith(builderBase);
      if (isExternal) {
        return (
          <a href={href} className={className}>
            {children}
          </a>
        );
      }
      let to = href.startsWith(builderBase) ? href.slice(builderBase.length) : href;
      if (!to.startsWith("/")) to = `/${to}`;
      return (
        <Link to={to} className={className}>
          {children}
        </Link>
      );
    },
    []
  );

  return (
    <ThemedOadsSidebar
      baseUrls={{
        platform: CONFIG.PLATFORM_BASE_URL,
        next: CONFIG.NEXT_BASE_URL,
        builder: CONFIG.BUILDER_BASE_URL,
      }}
      clientId={clientId ?? ""}
      client={{
        // Channel visibility (parity with platform-next / the redesigned channels).
        country: country?.code,
        isSelfServeEnabled: client?.isSelfServeEnabled ?? true,
        hasDmCoreCampaigns: client?.hasDmCoreCampaigns ?? false,
        isEddmOnly,
      }}
      currentPath={pathname}
      footerItems={oadsSidebarFooterItems}
      eddmLogo={isEddmOnly}
      collapseOnMount={collapseOnMount}
      renderLink={renderSidebarLink}
    />
  );
};
